import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { CompanyService, Company } from "./company.service";
import CompanyForm from "./CompanyForm";
import "./CompanyForm.css";
import { API_BASE_URL } from "../../../environment";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { showSuccessAlert, showErrorAlert } from "../../../utils/alertUtils";
import Activities from "../../../components/Tabs/Activities";
import Notes from "../../../components/Tabs/Notes";
import Files from "../../../components/Tabs/Files";
import RelatedFiles from "../../../components/Tabs/RelatedFiles";
import Tasks from "../../../components/Tabs/Tasks";
import Email from "../../../components/Tabs/Email";
import Tabs from "../../../components/Tabs/tabs";
import Contacts from "../../../components/Tabs/Contacts";
import male from "../../../assets/male.png";
import company from "../../../assets/company.png";
import { useEmailProviders } from "../../../hooks/useEmailProviders";
import ComposeEmail from "../../application/components/email/ComposeEmail";
import CustomFieldDisplay from "../../../components/CustomFieldDisplay";
import Tooltip from "@mui/material/Tooltip";
import SalesActivityModal from "../salesActivity/SalesActivityModal";

interface CompanyDataState {
  company_id: number;
  crm_entity_id?: number;
  name: string;
  address: string;
  combinedAddress: string;
  website: string;
  phone: string;
  annualRevenue: string;
  taxId: string;
  createdDate: string;
  createdBy: string;
  updatedDate: string;
  updatedBy: string;
  currency: string;
  timezone: string;
  employeeCount: string;
  assignedUserFullName: string;
  industryType: string;
  assignedTeamName: string;
  businessType: string;
  linkedin_url: string;
  facebook_url: string;
  twitter_url: string;
  loading: boolean;
  error: string | null;
  assignedTeam?: {
    team_name?: string;
    name?: string;
    manager?: {
      full_name?: string;
      name?: string;
    };
  };
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;
}

const CompaniesDetails = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const { id } = useParams();
  const companyId = id;
  const [openModal, setOpenModal] = useState(false);
  const [showSalesActivityModal, setShowSalesActivityModal] = useState(false);
  const authHeader = localStorage.getItem("authCred");
  const tenantId = localStorage.getItem("selectedTenantId");
  const [activitiesRefreshTrigger, setActivitiesRefreshTrigger] = useState(0);
  const [companyContactIds, setCompanyContactIds] = useState<number[]>([]);
  const [companyContacts, setCompanyContacts] = useState<any[]>([]);
  const [companyData, setCompanyData] = useState<CompanyDataState>({
    company_id: 0,
    name: "Loading...",
    address: "Loading address...",
    combinedAddress: "Loading address...",
    website: "",
    phone: "",
    annualRevenue: "",
    taxId: "",
    createdDate: "",
    createdBy: "",
    updatedDate: "",
    updatedBy: "",
    currency: "USD",
    timezone: "",
    employeeCount: "",
    assignedTeamName: "",
    assignedUserFullName: "",
    linkedin_url: "",
    facebook_url: "",
    twitter_url: "",
    industryType: "",
    businessType: "",
    loading: true,
    error: null as string | null,
  });

  const [fullCompanyData, setFullCompanyData] = useState<Company | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string>("");
  const [isEditCompanyOpen, setIsEditCompanyOpen] = useState(false);

  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);

  // Email functionality
  const { hasConnectedProviders, loading: emailProvidersLoading } =
    useEmailProviders();
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);
  const [composeOpenCounter, setComposeOpenCounter] = useState(0);
  const [showContactSelector, setShowContactSelector] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [emailDropdownRef, setEmailDropdownRef] =
    useState<HTMLDivElement | null>(null);

  // Function to fetch contact details for the company
  const fetchCompanyContacts = async () => {
    try {
      if (!companyId || !authHeader || !tenantId) return;

      const response = await fetch(
        `${API_BASE_URL}/contact/?company_id=${companyId}&tenant_id=${tenantId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${authHeader}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const contacts = data.results || [];
        const contactIds = contacts.map((contact: any) => contact.contact_id);

        setCompanyContacts(contacts);
        setCompanyContactIds(contactIds);
      }
    } catch (error) {
      console.error("Error fetching company contacts:", error);
    }
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        setCompanyData((prev) => ({ ...prev, loading: true }));

        if (!companyId) {
          throw new Error("Company ID not provided");
        }

        if (!authHeader || !tenantId) {
          throw new Error("Authentication required");
        }

        const companyResponse = await fetch(
          `${API_BASE_URL}/company/${companyId}/?tenant_id=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!companyResponse.ok) {
          if (companyResponse.status === 404) {
            navigate(route.companies);
            return;
          }
          throw new Error("Failed to fetch company");
        }

        const company = await companyResponse.json();

        setFullCompanyData(company);

        const addressParts = [
          company.address,
          company.city,
          company.state,
          company.country,
        ].filter(Boolean);
        const combinedAddress =
          addressParts.length > 0 ? addressParts.join(", ") : "";

        const industryTypeName =
          company.industry_type_display_name || "Not Set";
        const businessTypeName =
          company.business_type_display_name || "Not Set";

        const createdByName =
          typeof company.created_by === "object" && company.created_by
            ? company.created_by.full_name
            : company.created_by_username ||
            (company.created_by ? `User ID: ${company.created_by}` : "N/A");
        const updatedByName =
          typeof company.updated_by === "object" && company.updated_by
            ? company.updated_by.full_name
            : company.updated_by_username ||
            (company.updated_by ? `User ID: ${company.updated_by}` : "N/A");
        const assignedUserName =
          typeof company.assigned_user === "object" && company.assigned_user
            ? company.assigned_user.full_name
            : company.assigned_user_full_name || "";

        setCompanyData({
          company_id: company.company_id || 0,
          crm_entity_id: company.crm_entity_id,
          name: company.company_name || "Unknown Company",
          address: company.address || "",
          combinedAddress: combinedAddress,
          website: company.website || "",
          phone: company.phone || "",
          annualRevenue: company.annual_revenue || "",
          taxId: company.tax_id || "",
          createdDate: formatCustomDateTime(
            company.created_datetime || company.created_at || "Unknown"
          ),
          createdBy: createdByName,
          updatedDate: formatCustomDateTime(
            company.updated_datetime || company.updated_at || "Unknown"
          ),
          updatedBy: updatedByName,
          currency: company.currency_code || "",
          timezone: company.time_zone || "",
          employeeCount: company.employee_count || "",
          assignedTeamName: company.assigned_team?.team_name || "",
          assignedTeam: company.assigned_team || null,
          assignedUserFullName: assignedUserName,
          industryType: industryTypeName === "Not Set" ? "" : industryTypeName,
          businessType: businessTypeName === "Not Set" ? "" : businessTypeName,
          linkedin_url: company.linkedin_url || "",
          facebook_url: company.facebook_url || "",
          twitter_url: company.twitter_url || "",
          tags: company.tags || { insert: [] },
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error("Error fetching company details:", error);
        setCompanyData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load company details",
        }));
      }
    };

    fetchCompanyDetails();
  }, [companyId]);

  // Fetch contact details when company data is loaded
  useEffect(() => {
    if (companyData.company_id && !companyData.loading) {
      fetchCompanyContacts();
    }
  }, [companyData.company_id, companyData.loading]);

  // Reload company contacts after related updates (e.g., adding a contact)
  useEffect(() => {
    if (!companyData.loading) {
      fetchCompanyContacts();
    }
  }, [activitiesRefreshTrigger]);

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_company");

    const handleHidden = () => {
      setIsEditCompanyOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);

  const handleEditSubmit = async (formData: any) => {
    if (!fullCompanyData || isUpdating) return;

    setIsUpdating(true);
    setEditError("");

    try {
      let updatedCompany: Company;

      // Check if there's a logo file
      if (formData.logoFile) {
        // Create FormData and append all fields
        const submitFormData = new FormData();

        // Add all non-file fields
        Object.keys(formData).forEach((key) => {
          if (
            key !== "logoFile" &&
            formData[key] !== null &&
            formData[key] !== undefined
          ) {
            if (key === "custom_fields" && typeof formData[key] === "string") {
              // custom_fields is already stringified from CompanyForm
              submitFormData.append(key, formData[key]);
            } else {
              submitFormData.append(key, formData[key]);
            }
          }
        });

        // Add the logo file
        submitFormData.append("company_logo", formData.logoFile);

        updatedCompany = await CompanyService.updateCompanyWithLogo(
          fullCompanyData.company_id!,
          submitFormData
        );
      } else {
        updatedCompany = await CompanyService.updateCompany(
          fullCompanyData.company_id!,
          formData
        );
      }

      setFullCompanyData(updatedCompany);

      const updatedByName =
        typeof updatedCompany.updated_by === "object" &&
          updatedCompany.updated_by
          ? updatedCompany.updated_by.full_name
          : updatedCompany.updated_by_username ||
          (updatedCompany.updated_by
            ? `User ID: ${updatedCompany.updated_by}`
            : "N/A");

      setCompanyData((prev) => ({
        ...prev,
        name: updatedCompany.company_name || prev.name,
        address: updatedCompany.address || prev.address,
        combinedAddress: prev.combinedAddress,
        website: updatedCompany.website || prev.website,
        phone: updatedCompany.phone || prev.phone,
        annualRevenue: updatedCompany.annual_revenue || prev.annualRevenue,
        currency: updatedCompany.currency_code || prev.currency,
        timezone: updatedCompany.time_zone || prev.timezone,
        employeeCount: updatedCompany.employee_count || prev.employeeCount,
        businessType:
          updatedCompany.business_type_display_name || prev.businessType,
        industryType:
          updatedCompany.industry_type_display_name || prev.industryType,
        updatedDate: formatCustomDateTime(
          updatedCompany.updated_at || new Date().toISOString()
        ),
        updatedBy: updatedByName,
        linkedin_url: updatedCompany.linkedin_url || "",
        facebook_url: updatedCompany.facebook_url || "",
        twitter_url: updatedCompany.twitter_url || "",
      }));

      setTimeout(() => {
        closeOffcanvas("offcanvas_edit_company");
        showSuccessAlert(
          `Company "${updatedCompany.company_name}" updated successfully!`
        );
        setActivitiesRefreshTrigger((prev) => prev + 1);
      }, 100);
    } catch (error: any) {
      console.error("Error updating company:", error);
      setEditError("Failed to update company. Please try again.");
      showErrorAlert("Failed to update company. Please try again.");
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!fullCompanyData || isDeleting) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await CompanyService.deleteCompany(fullCompanyData.company_id!);

      setOpenModal(false);

      setShowDeleteLoader(true);

      setTimeout(() => {
        setShowDeleteLoader(false);
        showSuccessAlert(
          `Company "${fullCompanyData.company_name}" deleted successfully!`
        );
        setActivitiesRefreshTrigger((prev) => prev + 1);
        setTimeout(() => navigate(route.companies), 500);
      }, 1500);
    } catch (error: any) {
      console.error("Error deleting company:", error);
      setDeleteError(
        error.response?.data?.message ||
        "Failed to delete company. Please try again."
      );
      showErrorAlert(
        error.response?.data?.message ||
        "Failed to delete company. Please try again."
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteError("");
    setOpenModal(false);
  };

  // Email functionality
  const handleSendEmail = () => {
    if (companyContacts.length > 1) {
      // Toggle contact selector dropdown for multiple contacts
      setShowContactSelector(!showContactSelector);
    } else {
      // Directly open email composer for single contact
      setComposeOpenCounter((prev) => prev + 1);
      setIsComposeEmailOpen(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emailDropdownRef &&
        !emailDropdownRef.contains(event.target as Node)
      ) {
        setShowContactSelector(false);
      }
    };

    if (showContactSelector) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showContactSelector, emailDropdownRef]);

  const handleCloseComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const handleMinimizeComposeEmail = () => {
    // Handle minimize if needed
    setIsComposeEmailOpen(false);
  };

  const handleContactSelection = (contact: any, isChecked: boolean) => {
    if (isChecked) {
      setSelectedContacts((prev) => [...prev, contact]);
    } else {
      setSelectedContacts((prev) =>
        prev.filter((c) => c.contact_id !== contact.contact_id)
      );
    }
  };

  const handleConfirmContactSelection = () => {
    setShowContactSelector(false);
    setIsComposeEmailOpen(true);
  };

  const handleSelectAllContacts = () => {
    const contactsWithEmail = companyContacts.filter(
      (contact) => contact.primary_email || contact.email
    );
    setSelectedContacts(contactsWithEmail);
  };

  const handleDeselectAllContacts = () => {
    setSelectedContacts([]);
  };

  // Get the primary email for the company contacts
  const getPrimaryEmail = () => {
    // If contacts are selected, use their emails
    if (selectedContacts.length > 0) {
      const emails = selectedContacts
        .map((contact) => contact.primary_email || contact.email)
        .filter((email) => email)
        .join(", ");
      return emails;
    }

    // Fallback to original logic for single contact
    if (companyContacts.length > 0) {
      // Find the first contact with a primary email
      const contactWithEmail = companyContacts.find(
        (contact: any) => contact.primary_email
      );
      if (contactWithEmail) {
        return contactWithEmail.primary_email;
      }

      // If no primary email, try to find any email
      const contactWithAnyEmail = companyContacts.find(
        (contact: any) => contact.email
      );
      if (contactWithAnyEmail) {
        return contactWithAnyEmail.email;
      }
    }
    return "";
  };

  // Check if email functionality should be enabled
  const isEmailEnabled =
    !emailProvidersLoading && hasConnectedProviders && getPrimaryEmail() !== "";

  const getEmailDisabledReason = () => {
    if (emailProvidersLoading) return "Checking email integration...";
    if (!hasConnectedProviders) return "Email is not integrated";
    if (companyContacts.length === 0) return "No linked contact found for this company";
    if (!getPrimaryEmail()) return "Linked contact has no email address";
    return "Email is not available";
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const modal = document.getElementById(offcanvasId);
    if (modal) {
      const closeButton = modal.querySelector(
        '[data-bs-dismiss="offcanvas"]'
      ) as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        const bsOffcanvas = (window as any).bootstrap?.Offcanvas?.getInstance(
          modal
        );
        if (bsOffcanvas) {
          bsOffcanvas.hide();
        }
      }
    }
  };

  const copyCompanyToClipboard = async () => {
    try {
      const companyDetails = `
Company Details:
Name: ${companyData.name}
Website: ${companyData.website}
Phone: ${companyData.phone}
Address: ${companyData.address}
Tax ID: ${companyData.taxId}
Annual Revenue: ${companyData.annualRevenue}
Employee Count: ${companyData.employeeCount}
Industry Type: ${companyData.industryType}
Business Type: ${companyData.businessType}
Created: ${companyData.createdDate}
      `.trim();

      await navigator.clipboard.writeText(companyDetails);

      const shareButton = document.querySelector(
        '[data-action="share-company"]'
      ) as HTMLElement;
      if (shareButton) {
        const originalText = shareButton.innerHTML;
        shareButton.innerHTML =
          '<i class="ti ti-check me-1"></i>Copied to clipboard!';
        shareButton.style.color = "#28a745";

        setTimeout(() => {
          shareButton.innerHTML = originalText;
          shareButton.style.color = "";
        }, 2000);
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);

      const textArea = document.createElement("textarea");
      textArea.value = `
Company Details:
Name: ${companyData.name}
Website: ${companyData.website}
Phone: ${companyData.phone}
Address: ${companyData.address}
Tax ID: ${companyData.taxId}
Annual Revenue: ${companyData.annualRevenue}
Employee Count: ${companyData.employeeCount}
Industry Type: ${companyData.industryType}
Business Type: ${companyData.businessType}
Created: ${companyData.createdDate}
      `.trim();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }
  };

  const DeletePreloader = () => {
    return (
      <div
        className="preloader"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgb(255, 255, 255)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <div className="loader"></div>
        <p className="mt-3 text-muted">
          Company deleted successfully. Redirecting...
        </p>
      </div>
    );
  };

  // Show loading state
  if (companyData.loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Company Overview</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                  >
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-muted">Loading company details...</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (companyData.error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Company Overview</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="alert alert-danger">{companyData.error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Show delete loader overlay */}
      {showDeleteLoader && <DeletePreloader />}

      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">Company Overview</h4>
                  </div>
                  <div className="col-sm-8 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {/* Contact User */}
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.companies}>
                          <i className="ti ti-arrow-narrow-left" />
                          Company
                        </Link>
                      </li>
                      <li>
                        {companyData.name && companyData.name.length > 75 ? (
                          <Tooltip title={companyData.name} placement="top" arrow>
                            <p className="mb-1" style={{ cursor: "default" }}>
                              {`${companyData.name.substring(0, 75)}...`}
                            </p>
                          </Tooltip>
                        ) : (
                          <p className="mb-1">{companyData.name || "-"}</p>
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="card">
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div className="avatar avatar-xxl me-3 flex-shrink-0 border p-2">
                        {fullCompanyData?.company_logo ? (
                          <img
                            src={fullCompanyData.company_logo}
                            alt="Company Logo"
                            className="rounded"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "50%",
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center h-100 w-100 bg-light rounded">
                            <img
                              src={company}
                              alt="Company Logo"
                              className="w-auto h-auto"
                              style={{
                                width: "32px",
                                height: "32px",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-1">
                          {companyData.name && companyData.name.length > 100 ? (
                            <Tooltip title={companyData.name} placement="top" arrow>
                              <span className="d-block mb-1" style={{ cursor: "default" }}>
                                {`${companyData.name.substring(0, 100)}...`}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="d-block mb-1">{companyData.name || "-"}</span>
                          )}
                        </h5>
                        {companyData.combinedAddress && (
                          <p className="mb-2">
                            <i className="ti ti-map-pin-pin me-1"></i>
                            {companyData.loading ? (
                              <span className="placeholder-glow">
                                <span className="placeholder col-8"></span>
                              </span>
                            ) : (
                              companyData.combinedAddress
                            )}
                          </p>
                        )}
                        {companyData.error && (
                          <p className="text-danger mb-2">
                            <i className="ti ti-alert-circle me-1"></i>
                            {companyData.error}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="contacts-action">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {isEmailEnabled ? (
                          <div
                            className="position-relative"
                            ref={setEmailDropdownRef}
                          >
                            <button
                              className="btn btn-secondary d-flex align-items-center"
                              onClick={handleSendEmail}
                              title={
                                companyContacts.length > 1
                                  ? `Send email to ${companyContacts.length} contacts`
                                  : "Send Email"
                              }
                            >
                              <i className="ti ti-mail me-2" />
                              Send Email
                            </button>

                            {/* Contact Selector Dropdown */}
                            {showContactSelector &&
                              companyContacts.length > 1 && (
                                <div
                                  className="position-absolute top-100 end-0 bg-white border rounded-lg shadow-lg"
                                  style={{
                                    minWidth: "250px",
                                    zIndex: 1000,
                                    maxHeight: "400px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div className="p-3 border-bottom">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <h6 className="mb-0">Select Contacts</h6>
                                    </div>
                                  </div>
                                  <div
                                    className="p-3"
                                    style={{
                                      maxHeight: "280px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    {/* Select All Checkbox */}
                                    {companyContacts.filter(
                                      (contact) =>
                                        contact.primary_email || contact.email
                                    ).length > 0 && (
                                        <div className="mb-2 pb-2 border-bottom">
                                          <label
                                            className="d-flex align-items-center p-2 rounded hover-bg-light"
                                            style={{ cursor: "pointer" }}
                                          >
                                            <input
                                              type="checkbox"
                                              className="me-3"
                                              checked={
                                                selectedContacts.length ===
                                                companyContacts.filter(
                                                  (contact) =>
                                                    contact.primary_email ||
                                                    contact.email
                                                ).length
                                              }
                                              onChange={(e) =>
                                                e.target.checked
                                                  ? handleSelectAllContacts()
                                                  : handleDeselectAllContacts()
                                              }
                                            />
                                            <div className="flex-grow-1">
                                              <div className="fw-medium text-primary">
                                                Select All
                                              </div>
                                            </div>
                                          </label>
                                        </div>
                                      )}

                                    {companyContacts
                                      .filter(
                                        (contact) =>
                                          contact.primary_email || contact.email
                                      )
                                      .map((contact) => (
                                        <div
                                          key={contact.contact_id}
                                          className="mb-1"
                                        >
                                          <label
                                            className="d-flex align-items-center p-2 rounded hover-bg-light"
                                            style={{ cursor: "pointer" }}
                                          >
                                            <input
                                              type="checkbox"
                                              className="me-3"
                                              checked={selectedContacts.some(
                                                (c) =>
                                                  c.contact_id ===
                                                  contact.contact_id
                                              )}
                                              onChange={(e) =>
                                                handleContactSelection(
                                                  contact,
                                                  e.target.checked
                                                )
                                              }
                                            />
                                            <div className="flex-grow-1">
                                              <div className="fw-medium">
                                                {contact.first_name}{" "}
                                                {contact.last_name}
                                              </div>
                                              <div className="text-muted small">
                                                {contact.primary_email ||
                                                  contact.email}
                                              </div>
                                            </div>
                                          </label>
                                        </div>
                                      ))}
                                    {companyContacts.filter(
                                      (contact) =>
                                        contact.primary_email || contact.email
                                    ).length === 0 && (
                                        <div className="text-center text-muted py-3">
                                          <i className="ti ti-alert-circle fs-24 mb-2 d-block"></i>
                                          <p className="mb-0">
                                            No contacts with email addresses
                                            found.
                                          </p>
                                        </div>
                                      )}
                                  </div>
                                  <div className="p-3 border-top">
                                    <div className="d-flex justify-content-end">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-primary w-100"
                                        onClick={handleConfirmContactSelection}
                                        disabled={selectedContacts.length === 0}
                                      >
                                        Send Email
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>
                        ) : (
                          <Tooltip title={getEmailDisabledReason()} placement="top" arrow>
                            <span
                              className="btn btn-secondary d-flex align-items-center"
                              style={{ cursor: "not-allowed", opacity: 0.5 }}
                            >
                              <i className="ti ti-mail me-2" />
                              Send Email
                            </span>
                          </Tooltip>
                        )}
                        <Link
                          to="#"
                          className="btn-icon d-flex align-items-center justify-content-center"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_edit_company"
                          title="Edit Company"
                          onClick={() => setIsEditCompanyOpen(true)}
                        >
                          <i className="ti ti-edit-circle fs-16" />
                        </Link>
                        <Link
                          className="btn-icon d-flex align-items-center justify-content-center"
                          to="#"
                          onClick={(e) => { e.preventDefault(); setShowSalesActivityModal(true); }}
                          title="Log Activity"
                        >
                          <i className="ti ti-clipboard-list fs-16" />
                        </Link>
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="btn-icon d-flex align-items-center justify-content-center"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More Actions"
                          >
                            <i className="ti ti-dots-vertical fs-16" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end" style={{ width: 100 }}>
                            <Link
                              className="dropdown-item d-flex align-items-center"
                              to="#"
                              onClick={() => setOpenModal(true)}
                            >
                              <i className="ti ti-trash text-danger me-2" />
                              Delete
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Contact User */}
            </div>
            {/* Contact Sidebar */}
            <div className="col-xl-3 theiaStickySidebar">
              <div className="card">
                <div className="card-body p-3">
                  {/* Basic Information - Only show if there's basic data */}
                  {(companyData.website ||
                    companyData.phone ||
                    companyData.annualRevenue ||
                    companyData.taxId ||
                    companyData.createdDate) && (
                      <>
                        <h6 className="mb-3 fw-semibold">Basic Information</h6>
                        <div className="mb-3">
                          {companyData.website && (
                            <div className="d-flex align-items-center mb-3">
                              <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                                <i className="ti ti-globe" />
                              </span>
                              <p>
                                {companyData.loading ? (
                                  "Loading..."
                                ) : (
                                  <Link
                                    to={
                                      companyData.website.startsWith("http")
                                        ? companyData.website
                                        : `https://${companyData.website}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={companyData.website}
                                    className="text-decoration-none"
                                  >
                                    {companyData.website.length > 25
                                      ? `${companyData.website.substring(
                                        0,
                                        25
                                      )}...`
                                      : companyData.website}
                                  </Link>
                                )}
                              </p>
                            </div>
                          )}
                          {companyData.phone && (
                            <div className="d-flex align-items-center mb-3">
                              <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                                <i className="ti ti-phone" />
                              </span>
                              <p>
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.phone}
                              </p>
                            </div>
                          )}
                          {companyData.taxId && (
                            <div className="d-flex align-items-center mb-3">
                              <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                                <i className="ti ti-file-text" />
                              </span>
                              <p>
                                Tax ID:{" "}
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.taxId}
                              </p>
                            </div>
                          )}
                          {companyData.annualRevenue && (
                            <div className="d-flex align-items-center mb-3">
                              <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                                <i className="ti ti-currency-dollar" />
                              </span>
                              <p>
                                Annual Revenue:{" "}
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.annualRevenue}
                              </p>
                            </div>
                          )}
                          {companyData.createdDate && (
                            <div className="d-flex align-items-center mb-3">
                              <span className="avatar avatar-xs bg-light-300 p-0 flex-shrink-0 rounded-circle text-dark me-2">
                                <i className="ti ti-calendar-exclamation" />
                              </span>
                              <p>
                                Created on{" "}
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.createdDate}
                              </p>
                            </div>
                          )}
                        </div>
                        <CustomFieldDisplay
                          customFields={(fullCompanyData as any)?.custom_fields}
                          showTitle={true}
                        />
                      </>
                    )}
                  {/* Other Information - Only show if there's relevant data */}
                  {(companyData.industryType ||
                    companyData.businessType ||
                    companyData.timezone ||
                    companyData.employeeCount ||
                    companyData.assignedTeamName ||
                    companyData.assignedUserFullName ||
                    companyData.updatedDate) && (
                      <>
                        <hr />
                        <h6 className="mb-3 fw-semibold">Other Information</h6>
                        <ul>
                          {companyData.industryType && (
                            <li className="row mb-3">
                              <span className="col-6">Industry Type</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.industryType}
                              </span>
                            </li>
                          )}
                          {companyData.businessType && (
                            <li className="row mb-3">
                              <span className="col-6">Business Type</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.businessType}
                              </span>
                            </li>
                          )}
                          {/* {companyData.currency && (
                            <li className="row mb-3">
                              <span className="col-6">Currency</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.currency}
                              </span>
                            </li>
                          )} */}
                          {companyData.timezone && (
                            <li className="row mb-3">
                              <span className="col-6">Timezone</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.timezone}
                              </span>
                            </li>
                          )}
                          {companyData.employeeCount && (
                            <li className="row mb-3">
                              <span className="col-6">Employee Count</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.employeeCount + " Employees"}
                              </span>
                            </li>
                          )}

                          <li className="row mb-3">
                            <span className="col-6">Assigned Team</span>
                            <span className="col-6 d-flex align-items-center">
                              <span className="avatar avatar-xs me-1">
                                <i
                                  className="ti ti-users text-muted"
                                  style={{ fontSize: "0.85rem" }}
                                ></i>
                              </span>
                              <span>
                                {companyData.assignedTeam &&
                                  (companyData.assignedTeam.team_name ||
                                    companyData.assignedTeam.name)
                                  ? companyData.assignedTeam.team_name ||
                                  companyData.assignedTeam.name
                                  : "Not Assigned"}
                              </span>
                            </span>
                          </li>

                          <li className="row mb-3">
                            <span className="col-6">Assigned User</span>
                            <span className="col-6 d-flex align-items-center">
                              <span
                                className="avatar avatar-sm border rounded-circle p-1 me-2"
                                style={{ borderRadius: "50%" }}
                              >
                                {typeof fullCompanyData?.assigned_user ===
                                  "object" &&
                                  fullCompanyData?.assigned_user?.avatar_url ? (
                                  <img
                                    src={fullCompanyData.assigned_user.avatar_url}
                                    alt="Assigned User"
                                    className="rounded-circle"
                                    style={{
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <div className="d-flex align-items-center justify-content-center h-100 w-100 bg-light rounded-circle">
                                    <img src={male} alt="Assigned User" />
                                  </div>
                                )}
                              </span>
                              <span>
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.assignedUserFullName ||
                                  "Not Assigned"}
                              </span>
                            </span>
                          </li>

                          {companyData.updatedDate && (
                            <li className="row mb-3">
                              <span className="col-6">Last Modified</span>
                              <span className="col-6">
                                {companyData.loading
                                  ? "Loading..."
                                  : companyData.updatedDate}
                              </span>
                            </li>
                          )}
                        </ul>
                      </>
                    )}

                  <hr />
                  {/* Only show Social Profile section if any social media links exist */}
                  {!companyData.loading &&
                    (companyData.linkedin_url ||
                      companyData.facebook_url ||
                      companyData.twitter_url) && (
                      <>
                        <h6 className="mb-3 fw-semibold">Social Profile</h6>
                        <ul className="social-info">
                          {companyData.facebook_url && (
                            <li>
                              <Link
                                to={`https://${companyData.facebook_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fa-brands fa-facebook-f" />
                              </Link>
                            </li>
                          )}
                          {companyData.twitter_url && (
                            <li>
                              <Link
                                to={`https://${companyData.twitter_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fa-brands fa-twitter" />
                              </Link>
                            </li>
                          )}
                          {companyData.linkedin_url && (
                            <li>
                              <Link
                                to={`https://${companyData.linkedin_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fa-brands fa-linkedin-in" />
                              </Link>
                            </li>
                          )}
                        </ul>
                      </>
                    )}
                  <hr />


                  {/* Tags Section */}

                  <>
                    <h6 className="mb-3 fw-semibold">Tags</h6>
                    <ul className="social-info">
                      <li style={{ padding: '5px' }}>{
                        companyData.tags && companyData.tags.insert.length > 0 ? (
                          companyData.tags.insert.map((tag, index) => (
                            <span key={index}
                              className="badge me-1"
                              style={{
                                backgroundColor: '#824EEB',
                                color: 'white'
                              }}> {tag.entity_tag_name}</span>
                          ))
                        ) : (
                          <span className="text-muted">No Tags Assigned</span>
                        )
                      }</li>
                    </ul>
                  </>
                  <hr />



                  <h6 className="mb-3 fw-semibold">Settings</h6>
                  <Link
                    to="#"
                    className="d-block mb-3"
                    onClick={(e) => {
                      e.preventDefault();
                      copyCompanyToClipboard();
                    }}
                    data-action="share-company"
                  >
                    <i className="ti ti-share-2 me-1" />
                    Share Company
                  </Link>
                  <Link
                    to="#"
                    className="d-block mb-0"
                    onClick={() => setOpenModal(true)}
                  >
                    <i className="ti ti-trash-x me-1" />
                    Delete Company
                  </Link>
                </div>
              </div>

              {/* Record Information Card */}
              <div className="card mt-3">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">Record Information</h6>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Created Date</span>
                      <span className="col-6">
                        {companyData.loading
                          ? "Loading..."
                          : companyData.createdDate}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Created By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          {typeof fullCompanyData?.created_by === "object" &&
                            fullCompanyData?.created_by?.avatar_url ? (
                            <img
                              src={fullCompanyData.created_by.avatar_url}
                              alt="Created By"
                              className="rounded-circle"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 w-100 bg-light rounded-circle">
                              <img
                                src={male}
                                alt="Created By"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            </div>
                          )}
                        </span>
                        <span>
                          {companyData.loading
                            ? "Loading..."
                            : companyData.createdBy || "No User Found"}
                        </span>
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Updated Date</span>
                      <span className="col-6">
                        {companyData.loading
                          ? "Loading..."
                          : companyData.updatedDate}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Updated By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          {typeof fullCompanyData?.updated_by === "object" &&
                            fullCompanyData?.updated_by?.avatar_url ? (
                            <img
                              src={fullCompanyData.updated_by.avatar_url}
                              alt="Updated By"
                              className="rounded-circle"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 w-100 bg-light rounded-circle">
                              <img
                                src={male}
                                alt="Updated By"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  objectFit: "cover",
                                  borderRadius: "50%",
                                }}
                              />
                            </div>
                          )}
                        </span>
                        <span>
                          {companyData.loading
                            ? "Loading..."
                            : companyData.updatedBy || "No User Found"}
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            {/* /Contact Sidebar */}
            {/* Contact Details */}
            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0 pt-2">
                  <Tabs isCompanyPage />
                </div>
              </div>
              {/* Tab Content */}
              <div className="tab-content pt-0">
                {/* Activities */}

                <Activities
                  tenant_id={tenantId}
                  related_entity_id={companyData.company_id}
                  refreshTrigger={activitiesRefreshTrigger}
                  related_entity_name="company"
                  crm_entity_id={companyData.crm_entity_id}
                />

                {/* /Activities */}

                <Notes
                  tenant_id={tenantId}
                  related_entity_id={companyData.company_id}
                  related_entity_name="company"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Files
                  tenant_id={tenantId}
                  related_entity_id={companyData.company_id}
                  related_entity_name="company"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <RelatedFiles companyId={companyData.company_id} />

                <Tasks
                  tenant_id={tenantId}
                  related_entity_id={companyData.company_id}
                  related_entity_name="company"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Contacts
                  tenant_id={tenantId}
                  related_entity_id={companyData.company_id}
                  related_entity_name="company"
                  companyName={companyData.name}
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Email contactIds={companyContactIds} />
              </div>
              {/* /Tab Content */}
            </div>
            {/* /Contact Details */}
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Delete Contact */}
      <Modal show={openModal} onHide={handleDeleteCancel}>
        <div className="modal-body">
          <div className="text-center">
            <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
              <i className="ti ti-trash-x fs-36 text-danger" />
            </div>
            <h4 className="mb-2">Remove Company?</h4>
            <p className="mb-0">
              Are you sure you want to remove <br /> {companyData.name}?
            </p>

            {/* Error message */}
            {deleteError && (
              <div className="alert alert-danger mt-3" role="alert">
                <div className="d-flex align-items-center">
                  <i className="ti ti-alert-circle me-2"></i>
                  <span>{deleteError}</span>
                  <button
                    type="button"
                    className="btn-close ms-auto"
                    aria-label="Close"
                    onClick={() => setDeleteError("")}
                  ></button>
                </div>
              </div>
            )}

            <div className="d-flex align-items-center justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteCompany}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Deleting...
                  </>
                ) : (
                  "Yes, Delete it"
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
      {/* /Delete Contact */}

      <SalesActivityModal
        isVisible={showSalesActivityModal}
        onClose={() => setShowSalesActivityModal(false)}
        crmEntityId={companyData.crm_entity_id}
        recordId={companyData.company_id}
        onSuccess={() => setActivitiesRefreshTrigger((prev) => prev + 1)}
      />

      {/* Edit Company */}
      <div
        className="offcanvas offcanvas-end company-form-offcanvas"
        tabIndex={-1}
        id="offcanvas_edit_company"

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit Company</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        {editError && (
          <div
            className="alert alert-danger alert-dismissible fade show mx-3 mt-3"
            role="alert"
          >
            <strong>Error:</strong> {editError}
            <button
              type="button"
              className="btn-close"
              onClick={() => setEditError("")}
              aria-label="Close"
            ></button>
          </div>
        )}
        {isEditCompanyOpen && (
          <CompanyForm
            mode="edit"
            company={fullCompanyData}
            formId="edit_company_form_details"
            onSubmit={handleEditSubmit}
            onCancel={() => closeOffcanvas("offcanvas_edit_company")}
            loading={isUpdating}
          />
        )}
      </div>
      {/* /Edit Company */}
      {/* Compose Email Modal */}
      <ComposeEmail
        key={`compose-email-${composeOpenCounter}`}
        isOpen={isComposeEmailOpen}
        onClose={handleCloseComposeEmail}
        onMinimize={handleMinimizeComposeEmail}
        draftData={{
          send_to: getPrimaryEmail(),
          subject: `Re: ${companyData.name || "Company"}`,
        }}
        readOnlyRecipient={false}
      />
    </>
  );
};

export default CompaniesDetails;
